import { useState } from 'react'
import type { ReactNode } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/16/solid'

/** Lists up to this long show in full. */
export const FOLD_ABOVE = 5

/** A scrolling frame for a long table; pair it with `table-pin-rows` so the header stays in view. */
export const BOUNDED_TABLE = 'max-h-[50vh] overflow-auto rounded-lg border border-slate-200'

export type Folded<T> = { attention: T[]; ok: T[]; folds: boolean }

/** Rows that need attention and the rest, each in their order; a short list or one with nothing to hide does not fold. */
export const foldRows = <T,>(rows: readonly T[], needsAttention: (row: T) => boolean): Folded<T> => {
  const attention: T[] = []
  const ok: T[] = []
  for (const row of rows) {
    if (needsAttention(row)) {
      attention.push(row)
    } else {
      ok.push(row)
    }
  }
  return { attention, ok, folds: rows.length > FOLD_ABOVE && ok.length > 0 }
}

/**
 * A table body that keeps the rows needing attention in view and folds the others into one row
 * ("7 samples look good ▸") that expands them in place.
 */
export const FoldedBody = <T,>({
  rows,
  needsAttention,
  renderRow,
  colSpan,
  okLabel
}: {
  rows: readonly T[]
  needsAttention: (row: T) => boolean
  renderRow: (row: T) => ReactNode
  colSpan: number
  /** The folded row's text for `count` rows, e.g. "7 samples look good". */
  okLabel: (count: number) => string
}) => {
  const [open, setOpen] = useState(false)
  const folded = foldRows(rows, needsAttention)
  if (!folded.folds) {
    return <tbody>{rows.map(renderRow)}</tbody>
  }
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon
  return (
    <tbody>
      {folded.attention.map(renderRow)}
      <tr>
        <td colSpan={colSpan} className="bg-slate-50 p-0">
          <button
            type="button"
            className="flex w-full items-center gap-1 px-3 py-1.5 text-left text-xs text-slate-600 hover:text-slate-900"
            aria-expanded={open}
            data-testid="wizard-fold-toggle"
            onClick={() => setOpen((current) => !current)}
          >
            {okLabel(folded.ok.length)} <Chevron className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {open && folded.ok.map(renderRow)}
    </tbody>
  )
}
